/**
 * Errors, including the code a peer reports when it resets a stream or closes the session.
 *
 * @module
 */

import type { Milli } from "@moq/clock";
import { sharedStreamCode } from "./ietf/error.ts";
import type { IetfVersion } from "./ietf/version.ts";
import { TimeoutError } from "./util/timeout.ts";

/** The nominal brand for session termination codes. */
declare const SESSION_CODE: unique symbol;

/** A code from the session termination registry. */
export type SessionCode = number & { readonly [SESSION_CODE]: true };

/**
 * Codes a peer sends when terminating the session, mirroring the Rust `Session`.
 *
 * Specified by moq-lite, which reuses moq-transport's codes unchanged. Call `SessionCode(code)`
 * to construct an application code in the 64+ range. {@link StreamCode} is the other registry,
 * and the two are disjoint, so the same integer means different things in each.
 *
 * Codes 32-47 are reserved: nothing is sent there, and the draft gives a received one no
 * meaning, so treat anything not listed below as an unspecified error rather than guessing.
 *
 * @public
 */
export const SessionCode = Object.freeze(
	Object.assign((code: number): SessionCode => applicationCode(code) as SessionCode, {
		/** Ending the session normally, with no error. */
		Cancel: 0x0 as SessionCode,
		/** Something went wrong that isn't worth a dedicated code. */
		Internal: 0x1 as SessionCode,
		/** The credentials don't grant the requested path or operation. Retrying will fail again. */
		Unauthorized: 0x2 as SessionCode,
		/** The authorization token expired. Retrying with refreshed credentials may succeed. */
		Expired: 0x18 as SessionCode,
		/** A protocol rule was broken; the session is unusable. */
		ProtocolViolation: 0x3 as SessionCode,
		/** A request ID was reused or violated the negotiated draft’s order or parity rules. */
		InvalidRequestId: 0x4 as SessionCode,
		/** A key-value pair was malformed or repeated more than allowed. */
		KeyValueFormatting: 0x6 as SessionCode,
		/** The peer went past what the session allows: a request ID past MAX_REQUEST_ID, or too many announcements. */
		TooManyRequests: 0x7 as SessionCode,
		/** A PATH the peer may not send, such as a server sending one at all. */
		InvalidPath: 0x8 as SessionCode,
		/** The peer did not close within the GOAWAY drain deadline. */
		GoawayTimeout: 0x10 as SessionCode,
		/** A control message took too long. */
		Timeout: 0x11 as SessionCode,
		/** A token registration would exceed the advertised token cache size, which is 0 when unadvertised. */
		AuthTokenCacheOverflow: 0x13 as SessionCode,
		/** No version could be negotiated. */
		Version: 0x15 as SessionCode,
		/** The token could not be parsed. Retrying with the same token will fail again. */
		MalformedAuthToken: 0x16 as SessionCode,
		/** A token named an alias that was never registered. */
		UnknownAuthTokenAlias: 0x17 as SessionCode,
		/** An AUTHORITY the peer may not send, such as a server sending one at all. */
		InvalidAuthority: 0x19 as SessionCode,
		/** The peer cannot support the requested feature; moq-lite only. */
		Unsupported: 0x30 as SessionCode,
		/** The peer left more request updates unanswered than MAX_REQUEST_UPDATES allows. */
		TooManyRequestUpdates: 0x1b as SessionCode,
	} as const),
);

/** The nominal brand for stream reset codes. */
declare const STREAM_CODE: unique symbol;

/** A code from the stream reset registry. */
export type StreamCode = number & { readonly [STREAM_CODE]: true };

/**
 * Codes a peer sends when resetting a stream, mirroring the Rust `Stream`.
 *
 * The counterpart to {@link SessionCode}, and a disjoint space: a stream reset of 0 is
 * `StreamCode.Internal`, not a cancellation (`StreamCode.Cancel` is 1). Call
 * `StreamCode(code)` to construct an application code in the 64+ range.
 *
 * Conditions the shared codes don't cover are assigned in moq-lite's own 48-63 range, so a
 * received one is the named code. 32 through 47 is reserved: nothing is sent there and a
 * received one is an unspecified error.
 *
 * @public
 */
export const StreamCode = Object.freeze(
	Object.assign((code: number): StreamCode => applicationCode(code) as StreamCode, {
		/** Something went wrong that isn't worth a dedicated code. */
		Internal: 0x0 as StreamCode,
		/** The sender is done with this stream, not failing. A routine unsubscribe. */
		Cancel: 0x1 as StreamCode,
		/** The content missed its delivery deadline. */
		DeliveryTimeout: 0x2 as StreamCode,
		/** The session ended, taking this stream with it. */
		SessionClosed: 0x3 as StreamCode,
		/** The session is going away (a GOAWAY was received). */
		GoingAway: 0x4 as StreamCode,
		/** The reader fell too far behind and content was dropped to catch up. */
		TooFarBehind: 0x5 as StreamCode,
		/** The track's content could not be parsed. */
		MalformedTrack: 0x12 as StreamCode,
		/** The peer took too long to answer a control request. */
		ControlTimeout: 0x31 as StreamCode,
		/** The requested broadcast or track does not exist at the peer. */
		NotFound: 0x33 as StreamCode,
		/** The group was superseded by a newer one and dropped. */
		Old: 0x34 as StreamCode,
		/** The group was dropped under memory pressure, so it can be re-fetched. */
		Evicted: 0x35 as StreamCode,
		/** A frame declared a payload larger than the receiver accepts. */
		FrameTooLarge: 0x38 as StreamCode,
		/** The broadcast is neither announced nor served, so there is no route to it. */
		Unroutable: 0x36 as StreamCode,
		/** The grant does not cover this request, or no longer does. The session stays up. */
		Unauthorized: 0x3b as StreamCode,
		/** A group grew past its cache budget and was aborted. */
		GroupTooLarge: 0x32 as StreamCode,
		/** A frame's timedness or timestamp doesn't match its track's timescale. */
		TimestampMismatch: 0x39 as StreamCode,
	} as const),
);

function applicationCode(code: number): number {
	if (!Number.isInteger(code) || code < 64 || code > 0xffffffff) {
		throw new RangeError(`invalid application error code: ${code}`);
	}
	return code;
}

/**
 * An error the peer reported by closing the session, carrying its {@link SessionCode}.
 *
 * This surfaces on every transport, so catch this type rather than feature-detecting
 * `WebTransportError`, which a non-browser runtime never defines and the WebSocket fallback
 * never throws.
 *
 * ```ts
 * connection.error.subscribe((err) => {
 *   if (err instanceof Session && err.code === SessionCode.Unauthorized) {
 *     console.warn("server rejected the session");
 *   }
 * });
 * ```
 *
 * @public
 */
export class Session extends Error {
	/** The session code the peer sent, verbatim. */
	readonly code: SessionCode;

	constructor(code: SessionCode, options?: { cause?: unknown; reason?: string }) {
		super(options?.reason ? `remote error: ${code} (${options.reason})` : `remote error: ${code}`, options);
		this.name = "Session";
		this.code = code;
	}
}

/** Options for a {@link Stream}. */
export interface StreamOptions {
	/** The failure this one wraps. */
	cause?: unknown;
	/** The peer's human-readable reason, appended to the default message. */
	reason?: string;
	/** Replaces the default `remote error: <code>` text, for a condition raised locally. */
	message?: string;
}

/**
 * An error carrying a {@link StreamCode}: the code a peer sent when it reset a stream, or the
 * one this side sends when it resets one.
 *
 * This surfaces on every transport, so catch this type rather than feature-detecting
 * `WebTransportError`, which a non-browser runtime never defines and the WebSocket fallback
 * never throws. Local conditions with a code of their own subclass it ({@link TooFarBehind},
 * {@link FrameTooLarge}, {@link GroupTooLarge}, {@link TimestampMismatch}, {@link NotFound}), so the same `code` check catches a condition
 * whether it was raised here or reported by the peer.
 *
 * ```ts
 * try {
 *   frame = await group.readFrame();
 * } catch (err) {
 *   if (err instanceof Stream && err.code === StreamCode.Cancel) return;
 *   throw err;
 * }
 * ```
 *
 * @public
 */
export class Stream extends Error {
	/** The stream code; moq-lite resets forward it verbatim. */
	readonly code: StreamCode;

	constructor(code: StreamCode, options?: StreamOptions) {
		super(
			options?.message ??
				(options?.reason ? `remote error: ${code} (${options.reason})` : `remote error: ${code}`),
			options,
		);
		this.name = "Stream";
		this.code = code;
	}
}

/**
 * The reader asked for a frame the group never held, so the stream has a gap.
 *
 * Raised locally by a frame read, and decoded from a moq-lite peer's `TOO_FAR_BEHIND` reset, since a gap
 * reads the same either way.
 *
 * @public
 */
export class TooFarBehind extends Stream {
	constructor(options?: { cause?: unknown }) {
		super(StreamCode.TooFarBehind, {
			...options,
			message: "lagged: frames were evicted before being read",
		});
		this.name = "TooFarBehind";
	}
}

/**
 * A frame is larger than a group can cache, so appending it would exceed the budget by itself.
 *
 * Raised locally by a frame write, and decoded from a moq-lite peer's `FRAME_TOO_LARGE` reset.
 * Mirrors the Rust `Error::FrameTooLarge`, which rejects the same frame before touching any state.
 *
 * @public
 */
export class FrameTooLarge extends Stream {
	constructor(options?: { cause?: unknown }) {
		super(StreamCode.FrameTooLarge, {
			...options,
			message: "frame too large: larger than a group can cache",
		});
		this.name = "FrameTooLarge";
	}
}

/**
 * A write grew the group past its cache budget, so the group is aborted.
 *
 * Raised locally by a frame write, and decoded from a moq-lite peer's `GROUP_TOO_LARGE`
 * reset. Mirrors the Rust `Error::GroupTooLarge`.
 *
 * @public
 */
export class GroupTooLarge extends Stream {
	constructor(options?: { cause?: unknown }) {
		super(StreamCode.GroupTooLarge, {
			...options,
			message: "group too large: exceeded the cache budget",
		});
		this.name = "GroupTooLarge";
	}
}

/**
 * A frame's timestamp doesn't fit its track: a timestamp on a track with no timescale, none
 * on a track that has one, or a group's first frame starting before the group below it.
 *
 * Raised locally by a frame or datagram write, and decoded from a moq-lite peer's
 * `TIMESTAMP_MISMATCH` reset. Mirrors the Rust `Error::TimestampMismatch`.
 *
 * @public
 */
export class TimestampMismatch extends Stream {
	constructor(options?: { cause?: unknown }) {
		super(StreamCode.TimestampMismatch, {
			...options,
			message: "frame timestamp doesn't fit its track",
		});
		this.name = "TimestampMismatch";
	}
}

/**
 * The requested broadcast or track is not served here.
 *
 * @public
 */
export class NotFound extends Stream {
	constructor(what: string, options?: { cause?: unknown }) {
		super(StreamCode.NotFound, { ...options, message: `not found: ${what}` });
		this.name = "NotFound";
	}
}

/**
 * A peer's GOAWAY named a redirect the connection refuses, or one it could not parse.
 *
 * Terminal: the peer is leaving, so the connection stops rather than redialing the old
 * address. Mirrors the Rust `Error::RefusedRedirect`.
 *
 * @public
 */
export class RefusedRedirect extends Error {
	constructor(reason: string) {
		super(`GOAWAY redirect refused: ${reason}`);
		this.name = "RefusedRedirect";
	}
}

/**
 * The dial and the MoQ handshake together outlived the connect's `timeout` option.
 *
 * A caller can retry it. Mirrors the Rust `Error::ConnectTimeout`.
 *
 * @public
 */
export class ConnectTimeout extends Error {
	/** The timeout that elapsed. */
	readonly timeout: Milli;

	constructor(timeout: Milli) {
		super(`connect timed out after ${timeout}ms`);
		this.name = "ConnectTimeout";
		this.timeout = timeout;
	}
}

/**
 * A peer broke the protocol in a way the spec says must end the session.
 *
 * Thrown where the violation is detected, rather than handled there: a decoder has no session
 * to close. The dispatch that owns the session watches for it and closes, so a nonconforming
 * peer cannot repeat the violation on the next stream.
 *
 * @public
 */
export class ProtocolViolation extends Error {
	/** The session code to close with: `ProtocolViolation` unless the draft names a more specific one. */
	readonly code: SessionCode;

	constructor(message: string, options?: { cause?: unknown; code?: SessionCode }) {
		super(message, options);
		this.name = "ProtocolViolation";
		this.code = options?.code ?? SessionCode.ProtocolViolation;
	}
}

/** Package-internal compatibility names used by the wire implementation. */
export { Session as SessionError, Stream as StreamError, TooFarBehind as Lagged };

/** The WebTransport-shaped fields a stream reset code arrives in. */
type StreamErrorLike = { source?: unknown; streamErrorCode?: unknown };

function streamCode(err: unknown): StreamCode | undefined {
	if (typeof err !== "object" || err === null) return undefined;

	const { source, streamErrorCode } = err as StreamErrorLike;
	if (source !== "stream" || typeof streamErrorCode !== "number") return undefined;

	return streamErrorCode as StreamCode;
}

/** Transport context for encoding and decoding errors. @internal */
interface TransportErrorOptions {
	/** The negotiated IETF draft, or absent for moq-lite. */
	version?: IetfVersion;
}

/**
 * Which {@link StreamCode} to send when resetting a stream because of `err`.
 *
 * The counterpart to {@link fromTransport}, and the pair has to agree: a code we send for a
 * condition is the code we read that condition back from, or two peers disagree about what it
 * means. Mirrors the Rust `From<&Error> for Stream`.
 *
 * Lossy on purpose. An error describes what went wrong here, while the registry is what the peer
 * can act on, so anything without a code of its own degrades to {@link StreamCode | StreamCode.Internal}
 * rather than inventing one. A {@link Session} is {@link StreamCode | StreamCode.Unroutable}: a stream that
 * can still be reset outlived the session that failed, so that was the route it was served
 * through, and forwarding the session's code would mistranslate it across disjoint registries.
 * An application code is the exception, since both registries share the 64+ range. A session
 * that failed at the transport, with no code at all, is {@link StreamCode | StreamCode.Unroutable} too.
 *
 * On an IETF stream the code has to be one the negotiated draft assigns the same meaning to,
 * so a condition it does not register degrades to INTERNAL_ERROR as well. See
 * {@link sharedStreamCode}.
 *
 * @internal
 */
export function toStreamCode(err: unknown, options?: TransportErrorOptions): StreamCode {
	const code = localStreamCode(err);
	if (options?.version === undefined) return code;
	return sharedStreamCode(code) ? code : StreamCode.Internal;
}

/** The moq-lite code for a local failure, before any draft has a say. */
function localStreamCode(err: unknown): StreamCode {
	if (err instanceof Stream) return err.code;
	if (err instanceof TimeoutError) return StreamCode.DeliveryTimeout;
	// Session-scoped: the peer learns which rule it broke from the session close, not from here.
	if (err instanceof ProtocolViolation) return StreamCode.SessionClosed;
	// App codes share the 64+ range in both registries.
	if (err instanceof Session) return err.code >= 64 ? StreamCode(err.code) : StreamCode.Unroutable;
	// A session that failed underneath without a close code, as on a network or idle failure.
	const source = typeof err === "object" && err !== null ? (err as { source?: unknown }).source : undefined;
	if (source === "session") return StreamCode.Unroutable;
	return StreamCode.Internal;
}

/**
 * The transport refused a request stream or failed to grant stream credit before its deadline.
 *
 * This local failure is terminal and has no wire code. The request is not retried.
 */
export class NoStreamCredit extends Error {
	constructor(options?: ErrorOptions) {
		super("no stream credit", options);
		this.name = "NoStreamCredit";
	}
}

/**
 * The {@link StreamCode | StreamCode.ControlTimeout} error for a control request the peer never answered.
 *
 * A control request that outlives its deadline is not late content, so it does not go out as
 * {@link StreamCode | StreamCode.DeliveryTimeout} the way a bare {@link TimeoutError} would. Callers that
 * bound a request's response build this instead.
 *
 * @internal
 */
export function controlTimeout(cause: unknown): Stream {
	const message = cause instanceof Error && cause.message ? cause.message : "control request timed out";
	return new Stream(StreamCode.ControlTimeout, { cause, message });
}

/**
 * The {@link StreamCode | StreamCode.Unauthorized} error for a request the grant does not cover, naming
 * the broadcast. Unlike {@link SessionCode.Unauthorized}, it ends only this stream.
 *
 * @internal
 */
export function unauthorized(broadcast: string): Stream {
	return new Stream(StreamCode.Unauthorized, { message: `unauthorized: ${broadcast}` });
}

/**
 * Decode a transport failure into a {@link Stream} when it carries a stream reset code,
 * otherwise pass it through.
 *
 * Native WebTransport rejects with a `WebTransportError`; the WebSocket fallback mints an error
 * with the same `source`/`streamErrorCode` fields. Reading the fields rather than the class
 * covers both, and works in a runtime with no `WebTransportError` at all.
 *
 * On moq-lite, a code with a local class decodes back into it, so a peer's condition is caught by
 * the same `instanceof` as one raised here.
 *
 * On an IETF stream a code keeps its value unless moq-lite claims that number for something
 * the draft does not, such as its own 48-63 range. Those read as
 * {@link StreamCode | StreamCode.Internal} with the wire value kept in the message, since
 * {@link StreamCode} is one numeric space and cannot hold a foreign code without lying about
 * it. A code moq-lite names nothing for survives intact: nothing can misread it.
 *
 * @internal Called at the transport boundary so the raw error never reaches an application.
 */
export function fromTransport(err: unknown, options?: TransportErrorOptions): Error {
	const code = streamCode(err);
	if (code === undefined) return error(err);
	if (options?.version !== undefined && !sharedStreamCode(code) && claimedLocally(code)) {
		return new Stream(StreamCode.Internal, { cause: err, message: `remote error: ${code}` });
	}
	if (code === StreamCode.TooFarBehind) return new TooFarBehind({ cause: err });
	if (code === StreamCode.FrameTooLarge) return new FrameTooLarge({ cause: err });
	if (code === StreamCode.GroupTooLarge) return new GroupTooLarge({ cause: err });
	if (code === StreamCode.TimestampMismatch) return new TimestampMismatch({ cause: err });
	return new Stream(code, { cause: err });
}

/** The codes moq-lite names, which a foreign one must not borrow. */
const NAMED_CODES: ReadonlySet<number> = new Set(Object.values(StreamCode));

/**
 * Whether moq-lite could give `code` a meaning of its own: one of its named codes, or
 * anywhere in the 64+ range, where `StreamCode(code)` mints an application code and an
 * application compares against its own. moq-transport registers nothing above 0x12 and has
 * no application range at all, so a foreign code landing there is never what it looks like.
 */
function claimedLocally(code: number): boolean {
	return code >= 64 || NAMED_CODES.has(code);
}

const legacyWebTransportErrors = new WeakSet<object>();

/**
 * Build the `reason` to hand `abort()` / `cancel()` so the transport puts `code` on the wire.
 *
 * Both the WebTransport spec and the WebSocket fallback take the reset code from a
 * `WebTransportError`'s `streamErrorCode` and send 0 for anything else, a plain `Error`
 * included. Since 0 is {@link StreamCode | StreamCode.Internal}, cancelling with a bare `Error` tells the
 * peer we failed rather than that we are done.
 *
 * The native constructor exists exactly where native WebTransport does, which is where the
 * fallback isn't used, so mint a matching shape elsewhere rather than feature-detect a global
 * that will not be there.
 *
 * @internal
 */
export function toTransport(code: StreamCode, message: string): Error {
	const Native = (globalThis as { WebTransportError?: typeof WebTransportError }).WebTransportError;
	if (Native) {
		const Legacy = Native as unknown as new (init: { message: string; streamErrorCode: number }) => Error;
		if (legacyWebTransportErrors.has(Native)) return new Legacy({ message, streamErrorCode: code });

		try {
			return new Native(message, { source: "stream", streamErrorCode: code });
		} catch (err) {
			// Chromium still implements the previous single-dictionary constructor.
			if (!(err instanceof TypeError)) throw err;
			legacyWebTransportErrors.add(Native);
			return new Legacy({ message, streamErrorCode: code });
		}
	}

	return Object.assign(new Error(message), { source: "stream" as const, streamErrorCode: code });
}

/**
 * Decode a session close into its terminal error: `null` for a clean close
 * ({@link SessionCode.Cancel}), otherwise a {@link Session} carrying the peer's code.
 *
 * @internal Applied to the transport's `closed` info so the code survives to the application.
 */
export function fromClose(info: WebTransportCloseInfo): Session | null {
	const code = (info.closeCode ?? SessionCode.Cancel) as SessionCode;
	if (code === SessionCode.Cancel) return null;
	return new Session(code, { reason: info.reason });
}

// WebTransport rejects a close reason over 1024 bytes of UTF-8 by throwing, so a reason
// built from peer-supplied data has to be bounded before it gets there. A broadcast path
// is peer-supplied and long enough to reach this on its own.
const MAX_CLOSE_REASON = 1024;

/**
 * The longest prefix of `text` that fits a session close reason. `encodeInto` stops on a
 * whole code point, so `read` never lands mid-character the way slicing bytes would.
 *
 * @internal
 */
export function closeReason(text: string): string {
	const encoder = new TextEncoder();
	const buf = new Uint8Array(MAX_CLOSE_REASON);
	const { read } = encoder.encodeInto(text, buf);
	return text.slice(0, read);
}

/**
 * The session's close as the error it ends everything with, carrying the peer's code. A clean
 * close code is still an error here: whatever the close cut off did not end.
 *
 * @internal
 */
export function closeError(quic: WebTransport): Promise<Error> {
	return quic.closed.then(
		(info) => fromClose(info) ?? new Session(SessionCode.Cancel, { reason: info.reason }),
		(err: unknown) => error(err),
	);
}

/**
 * Whether a stream failed because its whole session closed, rather than on its own.
 *
 * @internal
 */
export function sessionClosed(err: unknown): boolean {
	return typeof err === "object" && err !== null && (err as { source?: unknown }).source === "session";
}

/**
 * Report a failure the session's close caused as the session's own error, which carries the
 * peer's close code; any other failure passes through.
 *
 * @internal
 */
export async function sessionCause(quic: WebTransport | undefined, err: unknown): Promise<Error> {
	if (quic && sessionClosed(err)) return closeError(quic);
	return error(err);
}

/**
 * Coerce an unknown thrown value into an `Error`.
 *
 * @internal
 */
export function error(err: unknown): Error {
	return err instanceof Error ? err : new Error(String(err));
}

/**
 * Format an error into a non-empty, human-readable string for logging.
 *
 * Safari always leaves `WebTransportError.message` blank, so a bare `err.message` degrades to
 * an empty string and the reason is lost. This falls back to the error type name and appends
 * the WebTransport `source` and application `streamErrorCode`, so the log line always says
 * something.
 */
export function reason(err: unknown): string {
	const e = error(err);

	// WebTransportError carries the failure origin and the peer's application error code,
	// often the only identifying detail since WebKit leaves `message` empty.
	if (typeof WebTransportError !== "undefined" && e instanceof WebTransportError) {
		const parts = [`source=${e.source}`];
		if (e.streamErrorCode !== null) parts.push(`code=${e.streamErrorCode}`);
		const detail = parts.join(" ");
		return e.message ? `${e.message} (${detail})` : `WebTransportError: ${detail}`;
	}

	return e.message || e.name || "unknown error";
}
